import React, { useState } from 'react';
import QRCode from 'react-qr-code';
import { renderToStaticMarkup } from 'react-dom/server';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, MapPin, Droplets, Download, Printer, CreditCard, RefreshCw, Wrench, AlertTriangle, MessageCircle, Upload, CheckCircle2, XCircle, Eye, Ban } from 'lucide-react';
import { toast } from 'sonner';
import AdminLayout from '../../layouts/AdminLayout';
import { ordersApi, paymentProofsApi } from '../../services/api';
import { formatCurrency, formatDate, formatDateTime, StatusBadge, PaymentBadge, formatApiError } from '../../utils/helpers';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { getKanbanColumns } from '../../config/businessTypes';
import PaymentModal from '../../components/PaymentModal';
import DiagnosisModal from '../../components/DiagnosisModal';
import WaMessageModal from '../../components/WaMessageModal';
import WaEmulator from '../../components/WaEmulator';

function WaLogsSection({ logs, t }) {
  const [selected, setSelected] = React.useState(null);
  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-[0_2px_10px_rgba(0,0,0,0.04)] overflow-hidden">
      <div className="flex items-center gap-2 px-5 py-4 border-b border-slate-50">
        <MessageCircle size={16} className="text-[#25D366]" />
        <h3 className="font-semibold text-slate-800 font-manrope text-sm">{t('Riwayat Pesan WA', 'WhatsApp History')}</h3>
        <span className="ml-auto text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">{logs.length}</span>
      </div>
      <div className="divide-y divide-slate-50">
        {logs.map(log => (
          <button key={log.id} onClick={() => setSelected(selected?.id === log.id ? null : log)}
            className={`w-full text-left px-5 py-3.5 hover:bg-slate-50 transition-colors ${selected?.id === log.id ? 'bg-emerald-50/40' : ''}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-slate-700 truncate">{log.message_preview || log.message?.slice(0, 80) || '—'}</p>
                <p className="text-xs text-slate-400 mt-0.5">{log.sent_by_name || '—'} · {log.created_at ? new Date(log.created_at).toLocaleString('id-ID', { day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit' }) : '—'}</p>
              </div>
              <span className={`text-xs px-1.5 py-0.5 rounded-full flex-shrink-0 ${log.status === 'sent' ? 'bg-green-100 text-green-700' : log.status === 'simulated' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-600'}`}>
                {log.status === 'sent' ? 'Terkirim' : log.status === 'simulated' ? 'Simulasi' : 'Gagal'}
              </span>
            </div>
          </button>
        ))}
      </div>
      {selected && (
        <div className="border-t border-slate-100 p-4 bg-slate-50/50 flex justify-center">
          <WaEmulator
            message={selected.message || selected.message_preview || ''}
            senderName={selected.target}
            timestamp={selected.created_at}
          />
        </div>
      )}
    </div>
  );
}

export default function OrderDetailPage() {
  const { id } = useParams();
  const { lang, t } = useLanguage();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showPayment, setShowPayment] = useState(false);
  const [showDiagnosis, setShowDiagnosis] = useState(false);
  const [showWa, setShowWa] = useState(false);
  const [showProofImage, setShowProofImage] = useState(null);
  const [confirmProof, setConfirmProof] = useState(null);
  const [confirmAmount, setConfirmAmount] = useState('');
  const { businessType, store } = useAuth();

  // Dynamic status flow from business type config
  const STATUS_FLOW = getKanbanColumns(businessType).map(col => ({
    value: col.id,
    label: typeof col.label === 'object' ? (lang === 'id' ? col.label.id : col.label.en) : col.label,
  }));

  const { data: order, isLoading } = useQuery({
    queryKey: ['order', id],
    queryFn: () => ordersApi.get(id).then(r => r.data),
  });

  const statusMutation = useMutation({
    mutationFn: (status) => ordersApi.updateStatus(id, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['order', id] });
      toast.success(t('Status diperbarui', 'Status updated'));
    },
    onError: err => toast.error(formatApiError(err)),
  });

  // Payment proofs
  const { data: proofs = [], refetch: refetchProofs } = useQuery({
    queryKey: ['payment-proofs', id],
    queryFn: () => paymentProofsApi.list(id).then(r => r.data),
    enabled: !!id,
  });

  const { data: waLogs = [] } = useQuery({
    queryKey: ['order-wa-logs', id],
    queryFn: () => ordersApi.getWaLogs(id).then(r => r.data),
    enabled: !!id,
  });

  const confirmMutation = useMutation({
    mutationFn: ({ proofId, amount }) => paymentProofsApi.confirm(id, proofId, amount),
    onSuccess: (res) => {
      toast.success('Pembayaran dikonfirmasi!');
      refetchProofs();
      queryClient.invalidateQueries({ queryKey: ['order', id] });
      setConfirmProof(null);
      setConfirmAmount('');
    },
    onError: err => toast.error(formatApiError(err)),
  });

  const rejectMutation = useMutation({
    mutationFn: (proofId) => paymentProofsApi.reject(id, proofId),
    onSuccess: () => { toast.success('Bukti ditolak'); refetchProofs(); },
    onError: err => toast.error(formatApiError(err)),
  });

  if (isLoading) return <AdminLayout><div className="flex items-center justify-center h-48"><div className="w-8 h-8 border-2 border-slate-200 border-t-[#0f6c44] rounded-full animate-spin" /></div></AdminLayout>;
  if (!order) return <AdminLayout><div className="text-center py-12 text-slate-400">{t('Pesanan tidak ditemukan', 'Order not found')}</div></AdminLayout>;

  const handleThermalPrint = () => {
    const storeInfo = store || {};
    const items = order.items || [];
    const trackingUrl = `${window.location.origin}/track/${order.tracking_code}`;
    const qrSvg = renderToStaticMarkup(<QRCode value={trackingUrl} size={80} level="M" />);
    const printContent = `
      <html><head><title>Nota ${order.tracking_code}</title>
      <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { font-family: 'Courier New', monospace; font-size: 11px; width: 76mm; padding: 4mm; }
        .center { text-align: center; }
        .line { border-top: 1px dashed #000; margin: 4px 0; }
        .row { display: flex; justify-content: space-between; margin: 1.5px 0; }
        .bold { font-weight: bold; }
        .big { font-size: 13px; }
        @media print { @page { size: 80mm auto; margin: 0; } body { width: 76mm; } }
      </style></head>
      <body>
        <div class="center bold big">${storeInfo.name || 'KasirWA'}</div>
        ${storeInfo.address ? `<div class="center">${storeInfo.address}</div>` : ''}
        ${storeInfo.phone ? `<div class="center">${storeInfo.phone}</div>` : ''}
        <div class="line"></div>
        <div class="row"><span>Kode</span><span class="bold">${order.tracking_code}</span></div>
        <div class="row"><span>Pelanggan</span><span>${order.customer_name || '-'}</span></div>
        <div class="row"><span>HP</span><span>${order.customer_phone || '-'}</span></div>
        <div class="line"></div>
        ${items.map(i => `<div class="row"><span>${i.product_name} x${i.quantity}${i.unit ? ' ' + i.unit : ''}</span><span>${new Intl.NumberFormat('id-ID', {style:'currency',currency:'IDR',minimumFractionDigits:0}).format(i.subtotal)}</span></div>`).join('')}
        <div class="line"></div>
        <div class="row bold"><span>Total</span><span>${new Intl.NumberFormat('id-ID', {style:'currency',currency:'IDR',minimumFractionDigits:0}).format(order.total_amount)}</span></div>
        <div class="row"><span>Terbayar</span><span>${new Intl.NumberFormat('id-ID', {style:'currency',currency:'IDR',minimumFractionDigits:0}).format(order.paid_amount || 0)}</span></div>
        ${(order.remaining_amount || 0) > 0 ? `<div class="row bold"><span>Sisa Bayar</span><span>${new Intl.NumberFormat('id-ID', {style:'currency',currency:'IDR',minimumFractionDigits:0}).format(order.remaining_amount)}</span></div>` : ''}
        <div class="line"></div>
        <div class="center">Terima kasih!</div>
        <div class="line"></div>
        <div class="center" style="margin-top:6px">
          <div style="width:80px;height:80px;margin:0 auto">${qrSvg}</div>
          <div style="font-size:9px;margin-top:3px;font-weight:bold">${order.tracking_code}</div>
          <div style="font-size:8px;color:#666">Scan untuk cek pesanan</div>
        </div>
      </body></html>
    `;
    const win = window.open('', '_blank', 'width=400,height=600');
    if (!win) return;
    win.document.write(printContent);
    win.document.close();
    win.focus();
    setTimeout(() => { win.print(); win.close(); }, 300);
  };

  return (
    <AdminLayout>
      <div className="max-w-4xl mx-auto space-y-5">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="p-2 rounded-xl hover:bg-slate-100 transition-colors">
            <ArrowLeft size={20} className="text-slate-600" />
          </button>
          <div className="flex-1">
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-2xl font-bold text-slate-900 font-manrope font-mono">{order.tracking_code}</h1>
              <StatusBadge status={order.status} lang={lang} size="md" />
              <PaymentBadge status={order.payment_status} lang={lang} />
            </div>
            <p className="text-slate-500 text-sm mt-0.5">{t('Dibuat', 'Created')}: {formatDateTime(order.created_at)}</p>
          </div>
          {order.customer_phone && (
            <button onClick={() => setShowWa(true)} data-testid="open-wa-button"
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-xl text-sm font-semibold hover:bg-green-700 active:scale-95 transition-all shadow-sm">
              <MessageCircle size={16} />WA
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {/* Diagnosis Warning Banner — tampil hanya untuk pesanan servis yang belum diinput harga */}
          {order.form_type === 'servis' && order.total_amount === 0 && (
            <div className="lg:col-span-3 bg-orange-50 border border-orange-200 rounded-2xl p-4 flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 bg-orange-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <AlertTriangle size={18} className="text-orange-600" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-orange-800">{t('Pesanan servis belum ada harga', 'Service order has no price yet')}</p>
                  <p className="text-xs text-orange-600 mt-0.5">{t('Input hasil diagnosa dan rincian biaya servis terlebih dahulu', 'Please input diagnosis and service cost breakdown first')}</p>
                </div>
              </div>
              <button onClick={() => setShowDiagnosis(true)} data-testid="open-diagnosis-button"
                className="flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-xl text-sm font-semibold hover:bg-orange-700 active:scale-95 transition-all flex-shrink-0">
                <Wrench size={15} />{t('Input Diagnosa & Harga', 'Input Diagnosis & Price')}
              </button>
            </div>
          )}

          {/* Diagnosis button for already-diagnosed orders */}
          {order.form_type === 'servis' && order.total_amount > 0 && (
            <div className="lg:col-span-3 bg-emerald-50 border border-emerald-100 rounded-2xl p-3.5 flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-emerald-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Wrench size={15} className="text-[#0f6c44]" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-emerald-800">{t('Pesanan Servis', 'Service Order')}</p>
                  {order.diagnosis_notes && <p className="text-xs text-emerald-600 mt-0.5 truncate max-w-sm">{order.diagnosis_notes}</p>}
                </div>
              </div>
              <button onClick={() => setShowDiagnosis(true)} data-testid="edit-diagnosis-button"
                className="flex items-center gap-2 px-3 py-1.5 bg-white border border-emerald-200 text-[#0f6c44] rounded-xl text-xs font-semibold hover:bg-emerald-50 transition-all flex-shrink-0">
                <Wrench size={13} />{t('Edit Diagnosa', 'Edit Diagnosis')}
              </button>
            </div>
          )}

          {/* Main Info */}
          <div className="lg:col-span-2 space-y-5">
            {/* Customer & Details */}
            <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-[0_2px_10px_rgba(0,0,0,0.04)]">
              <h3 className="font-semibold text-slate-800 mb-4 font-manrope">{t('Informasi Pesanan', 'Order Info')}</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-slate-400 mb-0.5">{t('Pelanggan', 'Customer')}</p>
                  <p className="font-semibold text-slate-800">{order.customer_name || '-'}</p>
                  {order.customer_phone && <p className="text-sm text-slate-500">{order.customer_phone}</p>}
                </div>
                <div>
                  <p className="text-xs text-slate-400 mb-0.5">{t('Kasir', 'Staff')}</p>
                  <p className="font-semibold text-slate-800">{order.staff_name || '-'}</p>
                </div>
                {order.parfum && (
                  <div className="flex items-center gap-2">
                    <Droplets size={16} className="text-slate-400" />
                    <div><p className="text-xs text-slate-400">Parfum</p><p className="text-sm font-medium text-slate-700">{order.parfum}</p></div>
                  </div>
                )}
                {order.shelf_location && (
                  <div className="flex items-center gap-2">
                    <MapPin size={16} className="text-slate-400" />
                    <div><p className="text-xs text-slate-400">{t('Lokasi Rak', 'Shelf')}</p><p className="text-sm font-medium text-slate-700">{order.shelf_location}</p></div>
                  </div>
                )}
                {order.estimated_completion && (
                  <div>
                    <p className="text-xs text-slate-400 mb-0.5">{t('Estimasi Selesai', 'Est. Completion')}</p>
                    <p className="text-sm font-medium text-slate-700">{formatDate(order.estimated_completion)}</p>
                  </div>
                )}
                {order.notes && <div className="col-span-2"><p className="text-xs text-slate-400 mb-0.5">{t('Catatan', 'Notes')}</p><p className="text-sm text-slate-700">{order.notes}</p></div>}
                {order.diagnosis_notes && (
                  <div className="col-span-2">
                    <p className="text-xs text-slate-400 mb-0.5">{t('Catatan Diagnosa', 'Diagnosis Notes')}</p>
                    <p className="text-sm text-slate-700">{order.diagnosis_notes}</p>
                  </div>
                )}
              </div>

              {/* Foto Kondisi Barang */}
              {order.condition_photos && order.condition_photos.length > 0 && (
                <div className="mt-4 pt-4 border-t border-slate-100">
                  <p className="text-xs text-slate-400 mb-2 font-medium">{t('Foto Kondisi Barang', 'Condition Photos')}</p>
                  <div className="flex flex-wrap gap-2">
                    {order.condition_photos.map((path, idx) => (
                      <a key={idx} href={`/api/uploads/photo/${path}`} target="_blank" rel="noreferrer"
                        className="w-20 h-20 rounded-xl overflow-hidden border border-slate-200 block hover:opacity-80 transition-opacity">
                        <img src={`/api/uploads/photo/${path}`} alt={`Foto ${idx + 1}`} className="w-full h-full object-cover" />
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Items */}
            <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-[0_2px_10px_rgba(0,0,0,0.04)]">
              <h3 className="font-semibold text-slate-800 mb-4 font-manrope">{t('Item Pesanan', 'Order Items')}</h3>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-100">
                      {['Item', t('Qty', 'Qty'), t('Harga', 'Price'), 'Subtotal'].map(h => (
                        <th key={h} className="text-left py-2 text-xs text-slate-400 font-semibold uppercase">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {(order.items || []).map((item, i) => (
                      <tr key={i} className="border-b border-slate-50 last:border-0">
                        <td className="py-3 text-sm font-medium text-slate-800">{item.product_name}</td>
                        <td className="py-3 text-sm text-slate-600">{item.quantity} {item.unit}</td>
                        <td className="py-3 text-sm text-slate-600">{formatCurrency(item.price)}</td>
                        <td className="py-3 text-sm font-semibold text-slate-800">{formatCurrency(item.subtotal)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t border-slate-100">
                      <td colSpan={3} className="py-3 text-sm font-bold text-slate-800">Total</td>
                      <td className="py-3 text-base font-extrabold text-[#0f6c44] font-manrope">{formatCurrency(order.total_amount)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>

            {/* Payments */}
            <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-[0_2px_10px_rgba(0,0,0,0.04)]">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-slate-800 font-manrope">{t('Riwayat Pembayaran', 'Payment History')}</h3>
                {order.payment_status !== 'paid' && (
                  <button onClick={() => setShowPayment(true)} data-testid="add-payment-button"
                    className="flex items-center gap-2 px-3 py-1.5 bg-[#0f6c44] text-white rounded-lg text-sm font-medium hover:bg-[#0c5636] transition-colors">
                    <CreditCard size={15} />{t('Catat Bayar', 'Add Payment')}
                  </button>
                )}
              </div>
              {(order.payments || []).length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-4">{t('Belum ada pembayaran', 'No payments yet')}</p>
              ) : (
                <div className="space-y-2">
                  {(order.payments || []).map((p, i) => (
                    <div key={i} className="flex items-center justify-between py-2.5 px-3 bg-slate-50 rounded-xl">
                      <div>
                        <p className="text-sm font-medium text-slate-800">{formatCurrency(p.amount)}</p>
                        <p className="text-xs text-slate-400">{p.payment_method?.toUpperCase()} · {p.payment_type} · {formatDateTime(p.created_at)}</p>
                      </div>
                      <p className="text-xs text-slate-500">{p.created_by_name}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Riwayat Pesan WA */}
            {waLogs.length > 0 && (
              <WaLogsSection logs={waLogs} t={t} />
            )}
            {proofs.length > 0 && (
              <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-[0_2px_10px_rgba(0,0,0,0.04)]">
                <div className="flex items-center gap-2 mb-4">
                  <Upload size={16} className="text-blue-500" />
                  <h3 className="font-semibold text-slate-800 font-manrope">{t('Bukti Pembayaran Masuk', 'Incoming Payment Proofs')}</h3>
                  <span className="ml-auto text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-semibold">
                    {proofs.filter(p => p.status === 'pending').length} pending
                  </span>
                </div>
                <div className="space-y-3">
                  {proofs.map(proof => (
                    <div key={proof.id} className={`border rounded-xl p-3.5 transition-colors ${
                      proof.status === 'pending' ? 'border-blue-200 bg-blue-50/30' :
                      proof.status === 'confirmed' ? 'border-green-200 bg-green-50/30' :
                      'border-red-200 bg-red-50/30'
                    }`}>
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-bold text-slate-800">{formatCurrency(proof.amount)}</span>
                            <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${
                              proof.status === 'pending' ? 'bg-blue-100 text-blue-700' :
                              proof.status === 'confirmed' ? 'bg-green-100 text-green-700' :
                              'bg-red-100 text-red-700'
                            }`}>{proof.status === 'pending' ? 'Menunggu' : proof.status === 'confirmed' ? 'Dikonfirmasi' : 'Ditolak'}</span>
                          </div>
                          <p className="text-xs text-slate-500 mt-1">{formatDateTime(proof.created_at)}</p>
                          {proof.notes && <p className="text-xs text-slate-600 mt-0.5 italic">"{proof.notes}"</p>}
                          {proof.status === 'confirmed' && <p className="text-xs text-green-600 mt-0.5">Dikonfirmasi oleh {proof.confirmed_by} · {formatCurrency(proof.confirmed_amount)}</p>}
                        </div>
                        <div className="flex gap-1.5 flex-shrink-0">
                          {proof.proof_image_url && (
                            <button onClick={() => setShowProofImage(proof.proof_image_url)}
                              className="p-1.5 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors" title="Lihat bukti">
                              <Eye size={14} className="text-slate-600" />
                            </button>
                          )}
                          {proof.status === 'pending' && (
                            <>
                              <button onClick={() => { setConfirmProof(proof); setConfirmAmount(String(proof.amount)); }}
                                data-testid={`confirm-proof-${proof.id}`}
                                className="p-1.5 bg-green-100 rounded-lg hover:bg-green-200 transition-colors" title="Konfirmasi">
                                <CheckCircle2 size={14} className="text-green-700" />
                              </button>
                              <button onClick={() => rejectMutation.mutate(proof.id)}
                                data-testid={`reject-proof-${proof.id}`}
                                className="p-1.5 bg-red-100 rounded-lg hover:bg-red-200 transition-colors" title="Tolak">
                                <XCircle size={14} className="text-red-600" />
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-4">
            {/* Payment Summary */}
            <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-[0_2px_10px_rgba(0,0,0,0.04)]">
              <h3 className="font-semibold text-slate-800 mb-3 font-manrope">{t('Ringkasan Bayar', 'Payment Summary')}</h3>
              <div className="space-y-2.5">
                {[
                  { label: 'Total', value: order.total_amount, bold: false },
                  { label: t('Terbayar', 'Paid'), value: order.paid_amount, bold: false },
                  { label: t('Sisa', 'Remaining'), value: order.remaining_amount, bold: true, highlight: order.remaining_amount > 0 },
                ].map(r => (
                  <div key={r.label} className={`flex justify-between ${r.bold ? 'pt-2 border-t border-slate-100' : ''}`}>
                    <span className={`text-sm ${r.bold ? 'font-bold text-slate-800' : 'text-slate-500'}`}>{r.label}</span>
                    <span className={`text-sm font-semibold ${r.highlight ? 'text-orange-600' : r.bold ? 'text-[#0f6c44] font-manrope text-base' : 'text-slate-800'}`}>{formatCurrency(r.value)}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Update Status */}
            <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-[0_2px_10px_rgba(0,0,0,0.04)]">
              <h3 className="font-semibold text-slate-800 mb-3 font-manrope">{t('Update Status', 'Update Status')}</h3>
              <div className="space-y-2">
                {STATUS_FLOW.map(s => (
                  <button key={s.value} onClick={() => statusMutation.mutate(s.value)} disabled={order.status === s.value || statusMutation.isPending}
                    className={`w-full px-3 py-2.5 rounded-xl text-sm font-medium text-left transition-colors ${order.status === s.value ? 'bg-[#0f6c44] text-white' : 'bg-slate-50 text-slate-600 hover:bg-slate-100'}`}>
                    {s.label}
                  </button>
                ))}
                {order.status !== 'dibatalkan' && <button onClick={() => { if (window.confirm('Batalkan pesanan ini? Stok barang akan dikembalikan.')) statusMutation.mutate('dibatalkan'); }} disabled={statusMutation.isPending} data-testid="cancel-order-button" className="w-full rounded-xl bg-rose-50 px-3 py-2.5 text-left text-sm font-semibold text-rose-600 transition-colors hover:bg-rose-100"><Ban size={15} className="mr-2 inline"/>Batalkan Pesanan</button>}
              </div>
            </div>

            {/* Download / Cetak Nota */}
            <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-[0_2px_10px_rgba(0,0,0,0.04)]">
              <h3 className="font-semibold text-slate-800 mb-3 font-manrope">{t('Download / Cetak Nota', 'Download / Print Receipt')}</h3>
              <div className="space-y-2">
                {/* A4 & A6 — PDF Download */}
                {['a4', 'a6'].map(fmt => (
                  <a key={fmt} href={ordersApi.getReceiptUrl(id, fmt)} target="_blank" rel="noreferrer"
                    data-testid={`download-receipt-${fmt}`}
                    className="flex items-center gap-2 px-3 py-2.5 bg-slate-50 text-slate-700 rounded-xl text-sm font-medium hover:bg-slate-100 transition-colors">
                    <Download size={15} className="text-slate-400" />
                    {fmt.toUpperCase()} — PDF Download
                  </a>
                ))}
                {/* Thermal — Browser Print */}
                <button onClick={handleThermalPrint}
                  data-testid="print-receipt-thermal"
                  className="w-full flex items-center gap-2 px-3 py-2.5 bg-slate-50 text-slate-700 rounded-xl text-sm font-medium hover:bg-slate-100 transition-colors text-left">
                  <Printer size={15} className="text-slate-400" />
                  Thermal (80mm) — Cetak Langsung
                </button>
                <a href={`/track/${order.tracking_code}`} target="_blank" rel="noreferrer"
                  className="flex items-center gap-2 px-3 py-2.5 bg-emerald-50 text-[#0f6c44] rounded-xl text-sm font-medium hover:bg-emerald-100 transition-colors">
                  <RefreshCw size={15} />
                  {t('Link Tracking', 'Tracking Link')}
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showPayment && (
        <PaymentModal order={order} onClose={() => setShowPayment(false)}
          onSuccess={() => { setShowPayment(false); queryClient.invalidateQueries({ queryKey: ['order', id] }); }} />
      )}

      {showDiagnosis && (
        <DiagnosisModal order={order} onClose={() => setShowDiagnosis(false)}
          onSuccess={() => { setShowDiagnosis(false); queryClient.invalidateQueries({ queryKey: ['order', id] }); }} />
      )}

      {showWa && (
        <WaMessageModal order={order} onClose={() => setShowWa(false)} onSent={() => {}} />
      )}

      {/* Proof Image Modal */}
      {showProofImage && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => setShowProofImage(null)}>
          <div className="bg-white rounded-2xl p-4 max-w-md w-full" onClick={e => e.stopPropagation()}>
            <p className="font-bold text-slate-800 mb-3">Bukti Pembayaran</p>
            <img src={`${process.env.REACT_APP_BACKEND_URL}${showProofImage}`} alt="Bukti" className="w-full rounded-xl object-contain max-h-96" />
            <button onClick={() => setShowProofImage(null)} className="w-full mt-3 py-2 bg-slate-100 rounded-xl text-sm font-semibold hover:bg-slate-200">Tutup</button>
          </div>
        </div>
      )}

      {/* Confirm Amount Modal */}
      {confirmProof && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setConfirmProof(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6" onClick={e => e.stopPropagation()}>
            <h3 className="font-bold text-slate-800 mb-1">Konfirmasi Pembayaran</h3>
            <p className="text-xs text-slate-500 mb-4">Nominal dari pelanggan: <strong>{formatCurrency(confirmProof.amount)}</strong>. Anda bisa mengedit jika perlu.</p>
            <label className="text-xs font-semibold text-slate-600 block mb-1">Nominal Dikonfirmasi</label>
            <input type="number" value={confirmAmount} onChange={e => setConfirmAmount(e.target.value)}
              className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:border-[#0f6c44] bg-slate-50 mb-4" />
            {confirmProof.proof_image_url && (
              <img src={`${process.env.REACT_APP_BACKEND_URL}${confirmProof.proof_image_url}`} alt="Bukti" className="w-full rounded-xl object-contain max-h-48 mb-4 border" />
            )}
            <div className="flex gap-2">
              <button onClick={() => { setConfirmProof(null); setConfirmAmount(''); }} className="flex-1 py-2.5 bg-slate-100 text-slate-700 rounded-xl text-sm font-semibold hover:bg-slate-200">Batal</button>
              <button onClick={() => confirmMutation.mutate({ proofId: confirmProof.id, amount: parseFloat(confirmAmount) })}
                disabled={confirmMutation.isPending || !confirmAmount}
                className="flex-1 py-2.5 bg-[#0f6c44] text-white rounded-xl text-sm font-semibold hover:bg-[#0c5636] disabled:opacity-60 flex items-center justify-center gap-1">
                {confirmMutation.isPending ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : 'Konfirmasi'}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
